// A cardio activity as a session in the history: the manual "Log an activity" form, a GPX or FIT import
// and the MCP coach all save it this way, so the history, XP, the streak and Progress read one shape.
import type { SessionLog } from '../session/log';
import type { CardioActivity } from './activity';
import { sportExerciseId, sportLabel } from './sports';

/** An activity shorter than this is saved and earns XP, but does not count as a session for the streak and the weekly goal. */
export const MIN_COUNTED_SEC = 10 * 60;

/** The workout id of a logged activity ("activity_running"); no library workout has it. */
export const activityWorkoutId = (a: Pick<CardioActivity, 'sport'>): string => `activity_${a.sport}`;

export const isActivityWorkoutId = (id: string | null | undefined): boolean => !!id && id.startsWith('activity_');

/**
 * The session log of an activity: one main block on the sport's catalog exercise with the moving time, plus
 * the activity itself. Counts as a session from 10 minutes on.
 */
export function activitySessionLog(activity: CardioActivity, startedAt: number, opts: { name?: string; historyOnly?: boolean } = {}): SessionLog & { activity: CardioActivity } {
  const sec = activity.durationSec;
  const endedAt = startedAt + Math.round((activity.elapsedSec ?? sec) * 1000);
  const counted = sec >= MIN_COUNTED_SEC;
  const workoutId = activityWorkoutId(activity);
  return {
    id: `${workoutId}-${startedAt}`,
    workoutId,
    workoutName: opts.name?.trim().slice(0, 120) || sportLabel(activity.sport, activity.variant),
    startedAt,
    endedAt,
    activeSec: sec,
    pausedSec: 0,
    plannedSec: sec,
    completed: counted,
    mainDone: counted,
    sections: [{ sectionId: 'main', kind: 'main', format: { kind: 'list' }, plannedSec: sec, doneSec: sec, skipped: false, roundsDone: null, partialRound: null }],
    blocks: [{
      sectionId: 'main', blockId: 'main_1', exerciseId: sportExerciseId(activity.sport, activity.variant),
      planned: { reps: null, seconds: sec, times: 1 }, done: { reps: 0, seconds: sec, times: 1 },
      skipped: false, startedAt, endedAt, weightKg: null, entries: [],
    }],
    activity,
    ...(opts.historyOnly ? { historyOnly: true } : {}),
  };
}
